import { Injectable, inject } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment.development';
import { AuthenticationService } from './Authentication/authentication.service';
import { ToastService } from './toast.service';
import { TicketService } from './ticket.service';
import { NotificationService } from './notification.service';
import { DesktopNotificationService } from './desktop-notification.service';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket: Socket | null = null;
  private readonly authService = inject(AuthenticationService);
  private readonly toastService = inject(ToastService);
  private readonly ticketService = inject(TicketService);
  private readonly notificationService = inject(NotificationService);
  private readonly desktopNotificationService = inject(DesktopNotificationService);

  private getOrCreateSessionId(): string {
    let sid = sessionStorage.getItem('tms_client_session_id');
    if (!sid) {
      sid = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('tms_client_session_id', sid);
    }
    return sid;
  }

  init(): void {
    const user = this.authService.currentUser();
    if (!user) return;

    if (this.socket) {
      this.socket.disconnect();
    }

    const wsBaseUrl = environment.wsUrl || 'http://localhost:3000';
    const isGuest = user.username === 'guest' || user.name === 'Guest User' || String(user.id) === '1';
    const sessionId = this.getOrCreateSessionId();

    this.socket = io(`${wsBaseUrl}/ws`, {
      query: {
        userId: user.id,
        deptId: user.departmentId || '',
        sessionId: sessionId,
        isGuest: String(isGuest),
      },
      // transports: ['websocket', 'polling'],
      transports: ['polling'], // only polling - no websocket support
    });

    // Targeted notification event (personal alerts & toasts)
    this.socket.on('ticket:notification', (data: any) => {
      this.notificationService.addLiveNotification(data);

      const title = data?.notification?.title || data?.title || 'Ticket Notification';
      const message = data?.notification?.message || data?.message || 'New update received.';
      const ticketId = data?.notification?.ticketId || data?.ticketId;

      this.toastService.info(title, message);

      // Trigger OS Desktop Notification
      this.desktopNotificationService.sendNotification({
        title,
        body: message,
        ticketId,
        tag: `ticket-${ticketId || Date.now()}`,
      });

      // Live update workspace, comments, activities & dashboards without refresh
      this.ticketService.loadTickets().subscribe();
      if (ticketId) {
        this.ticketService.triggerWorkspaceRefresh(ticketId);
      }
    });

    // Real-time workspace synchronization event (data refresh without popups)
    this.socket.on('ticket:workspace_refresh', (data: any) => {
      this.ticketService.loadTickets().subscribe();
      if (data?.ticketId) {
        this.ticketService.triggerWorkspaceRefresh(data.ticketId);
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

